import React, { useCallback, useEffect, useState } from 'react';
import { signOut } from 'firebase/auth';
import styled from 'styled-components';

import { useAuth } from '../firebase/FirebaseContext';
import {
  getImpersonator,
  isImpersonating,
  markNormal,
  subscribeImpersonation,
} from '../auth/impersonation';

/**
 * The persistent "viewing as X" strip for admin impersonation sessions
 * (`?becomeToken=` — becomeUser.ts). Keyed on the impersonation module, whose
 * truth source is the `impersonator` ID-token claim, so it survives reloads
 * for the whole life of the session. Deliberately loud and un-dismissable:
 * the ONLY way to make it go away is Exit, which ends the impersonated
 * session (signOut + full reload so every auth-reactive listener drops the
 * impersonated uid). Debug chrome — no Storybook story on purpose (every
 * Happo snapshot bills, and this never renders for real users).
 */

const Strip = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* Above the consent banner and every sheet/overlay — this must never be
     covered, or an impersonated session stops looking like one. */
  z-index: 2147483000;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 6px 12px;
  background: #4c1d95;
  color: #fff;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 13px;
  line-height: 1.3;
`;

const ExitButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 14px;
  padding: 2px 12px;
  background: transparent;
  color: #fff;
  font: inherit;
  cursor: pointer;
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

export const ImpersonationBanner: React.FC = () => {
  const auth = useAuth();
  const [, bump] = useState(0);

  useEffect(() => subscribeImpersonation(() => bump((n) => n + 1)), []);

  const onExit = useCallback(async () => {
    // Clear the marker first so the reload boots as a plainly normal session
    // even if the signOut below throws.
    markNormal();
    try {
      await signOut(auth);
    } catch {
      // Fall through to the reload — a stale credential beats a stuck banner.
    }
    window.location.assign('/');
  }, [auth]);

  if (!isImpersonating()) return null;

  const user = auth.currentUser;
  const target = user ? (user.email ?? user.uid) : 'signing in…';
  const actor = getImpersonator();

  return (
    <Strip data-testid="impersonation-banner" role="status">
      <span>
        Viewing as <strong>{target}</strong>
        {actor ? ` (impersonated by ${actor})` : ''}
      </span>
      <ExitButton type="button" onClick={() => void onExit()} data-testid="impersonation-exit">
        Exit
      </ExitButton>
    </Strip>
  );
};

export default ImpersonationBanner;
